import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { Icon, Modal } from '@instinet-analytics/semantic-ui-components';
import { AddWidgetModalBody } from 'appToolbar/AddWidgetModal/AddWidgetModalBody';
import './AddWidgetModal.scss';
import { WidgetDropdownItem } from 'widgets/WidgetsService';
import ManagedLayout from 'layoutManagement/ManagedLayout';
import { useDispatchRequestAddWidget } from 'layoutRenderer/store/dispatcher';
import { WidgetType } from 'widgets/WidgetType';
import { useSelectedAddWidgetItems } from 'appToolbar/AddWidgetModal/useSelectedAddWidgetItems';
import { useAddLayoutDragSourcePubSub } from '../AddLayoutDragSourcePubSub';
import { PubSub } from '@instinet-analytics/core-lib';
import { useServices } from 'app/hooks.ts';
import { WidgetId } from 'widgets/WidgetId.ts';

export interface AddWidgetSelectedItem {
    widget: WidgetDropdownItem;
    layout?: ManagedLayout;
}

export function AddWidgetModal(props: {
    onOpenAddWidgetModalPubSub: PubSub<void>;
}) {
    const [previewItem, setPreviewItem] = useState<
        AddWidgetSelectedItem | undefined
    >();

    const { clickstreamService, loggingService } = useServices();

    const [addMultiple, setAddMultiple] = useState<boolean>(false);

    const [openClassName, setOpenClassName] = useState<string>();

    const addLayoutDragSourcePubSub = useAddLayoutDragSourcePubSub();

    const triggerRef = useRef<HTMLDivElement | null>(null);

    const trigger = (
        <div
            className={classNames('AddWidgetModal-trigger', openClassName)}
            ref={triggerRef}
        >
            <Icon name="plus" />
            Add Widget
        </div>
    );

    const [draggingOntoLayout, setDraggingOntoLayout] = useState(false);

    const {
        selectedItems,
        addSelectedItem,
        removeSelectedItem,
        resetSelectedItems,
    } = useSelectedAddWidgetItems();

    const onOpen = () => {
        setPreviewItem(undefined);
        resetSelectedItems();
        setOpenClassName('modal-open');
        setAddMultiple(false);
    };

    const onToggleAddMultiple = useCallback(() => {
        const nextAddMultiple = !addMultiple;
        setAddMultiple(nextAddMultiple);
        if (!nextAddMultiple && selectedItems.length > 0) {
            setPreviewItem(selectedItems[0]);
            resetSelectedItems(selectedItems[0]);
        }
    }, [
        addMultiple,
        setAddMultiple,
        selectedItems,
        resetSelectedItems,
        setPreviewItem,
    ]);

    const onClickItem = useCallback(
        (item: AddWidgetSelectedItem) => {
            if (selectedItems.find((s) => s.widget.type === item.widget.type)) {
                removeSelectedItem(item);
            } else if (addMultiple) {
                addSelectedItem(item);
            } else {
                resetSelectedItems(item);
            }
            setPreviewItem(item);
        },
        [
            addSelectedItem,
            addMultiple,
            removeSelectedItem,
            resetSelectedItems,
            setPreviewItem,
            selectedItems,
        ]
    );

    const onClickChangeLayout = useCallback(
        (item: AddWidgetSelectedItem) => {
            addSelectedItem(item);
            setPreviewItem(item);
        },
        [addSelectedItem, setPreviewItem]
    );

    const dispatchRequestAddWidget = useDispatchRequestAddWidget();

    const addWidgets = (
        widgets: Array<{ widget: WidgetType; layout?: ManagedLayout }>
    ) => {
        dispatchRequestAddWidget(widgets);
        setOpenClassName(undefined);
    };

    const onSave = () => {
        if (selectedItems.length > 0) {
            addWidgets(
                selectedItems.map((i) => ({
                    widget: i.widget.type,
                    layout: i.layout,
                }))
            );
        }
    };

    const onCancel = () => {
        setOpenClassName(undefined);
        resetSelectedItems();
    };

    const onClickEnter = () => {
        onSave();
    };

    const closePubSubRef = useRef(new PubSub<void>());

    const onClickAddItem = useCallback(
        (wt: WidgetType, layout?: ManagedLayout) => {
            addWidgets([{ widget: wt, layout }]);
            closePubSubRef.current.publish();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [closePubSubRef]
    );

    const openModal = () => {
        triggerRef.current!.click();
    };

    const onWidgetDraggedOntoLayout = useCallback(
        (id?: WidgetId) => {
            setDraggingOntoLayout(false);
            if (!addMultiple) {
                closePubSubRef.current.publish();
            }
            const widgetType = id?.widgetType;

            if (widgetType) {
                loggingService.debug(
                    'AddWidgetModel',
                    'Recording widget dragged onto layout',
                    widgetType
                );

                clickstreamService.recordWidgetUsage(widgetType);
            }
        },
        [setDraggingOntoLayout, addMultiple, clickstreamService, loggingService]
    );

    useEffect(() => {
        const id = 'AddWidgetModal_' + uuid();
        addLayoutDragSourcePubSub.subscribeDragging(id, () =>
            setDraggingOntoLayout(true)
        );
        addLayoutDragSourcePubSub.subscribeDragged(
            id,
            onWidgetDraggedOntoLayout
        );
        props.onOpenAddWidgetModalPubSub.subscribe('AddWidgetModal', openModal);
        return () => {
            addLayoutDragSourcePubSub.unsubscribeDragging(id);
            addLayoutDragSourcePubSub.unsubscribeDragged(id);
        };
    }, [
        addLayoutDragSourcePubSub,
        props.onOpenAddWidgetModalPubSub,
        onWidgetDraggedOntoLayout,
    ]);

    const disableSaveButton = selectedItems.length === 0;

    const saveButtonText =
        addMultiple && selectedItems.length > 0
            ? `Add (${selectedItems.length})`
            : 'Add';

    const cssClasses = classNames(
        'AddWidgetModal',
        draggingOntoLayout && 'dragging-widget'
    );

    return (
        <Modal
            className={cssClasses}
            closePubSub={closePubSubRef.current}
            trigger={trigger}
            cancelText="Cancel"
            headerText="Add Widget"
            saveText={saveButtonText}
            allowCancel={true}
            disableSaveButton={disableSaveButton}
            onSave={onSave}
            onOpen={onOpen}
            onCancel={onCancel}
        >
            <AddWidgetModalBody
                addMultiple={addMultiple}
                previewItem={previewItem}
                selectedItems={selectedItems}
                onClickItem={onClickItem}
                onChangeLayout={onClickChangeLayout}
                onClickEnter={onClickEnter}
                onClickAddItem={onClickAddItem}
                onToggleAddMultiple={onToggleAddMultiple}
            />
        </Modal>
    );
}
